/**
 * Insurance-card upload helpers.
 *
 * Reuses the exact same sealed-box encryption and image downscale pipeline
 * as the insurer-letter upload (letterUpload.js).  The only difference is:
 *   1. The sealed JSON envelope carries `doc_kind: "insurance_card"` so the
 *      bridge (and bridge_sync) route it to the OCR intake, not the
 *      determination intake.
 *   2. The bridge upload endpoint is the same /letter-upload route (we're
 *      extending the letter shape, not adding new plumbing).
 *
 * Patient UX contract
 * -------------------
 * Upload is FIRE-AND-FORGET from the patient's perspective:
 *   - Immediately after upload: show "We'll read the card — please still
 *     check the numbers below." and continue the flow.
 *   - Do NOT block form completion on OCR finishing (OCR is async; the
 *     patient may submit the form before the Mac processes the card).
 *   - The physician sees OCR candidates in the pending-profile-edits chip,
 *     alongside the patient's manually typed values.
 */

import { sealSubmission, getBridgeSession } from "./bridgeClient";
import { prepareFileForUpload } from "./letterUpload";

const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

/**
 * Seal and upload an insurance card image to the bridge.
 *
 * Extends the letter-upload contract with `doc_kind: "insurance_card"` in
 * both the sealed envelope (for the Mac sync agent) and the outer request
 * body (for the bridge to store and pass through in the pending row).
 *
 * @param {File} file   The raw image file the patient selected.
 * @param {object} [sessionOverride]  If provided, use this session instead
 *   of the one stored in sessionStorage.
 * @returns {Promise<{ letter_id: string, received_at: string }>}
 */
interface BridgeSession {
  sessionToken?: string;
  assignmentId?: string;
  pubkeyB64?: string;
  [key: string]: unknown;
}

export async function sealAndUploadInsuranceCard(
  file: File,
  sessionOverride?: BridgeSession | null,
): Promise<{ letter_id: string; received_at: string }> {
  // `any` after the manual guard below: session is untyped (.js source) or an
  // override, guarded by hand for the three keys used.
  const session: any = sessionOverride || getBridgeSession();
  if (!session?.sessionToken || !session?.assignmentId || !session?.pubkeyB64) {
    throw new Error("No active bridge session — cannot upload insurance card");
  }

  // Reuse the exact same downscale + byte-read pipeline as letter upload.
  const { bytes, mime, filename } = await prepareFileForUpload(file);

  // Sealed envelope shape: extends the letter envelope with doc_kind.
  const cardPayload = {
    __letter__: true,        // bridge_sync looks for this flag
    doc_kind: "insurance_card",
    b64: bytesToBase64(bytes),
    mime,
    filename,
  };

  const ciphertextB64 = sealSubmission(cardPayload, session.pubkeyB64);

  const resp = await fetch(
    `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(session.assignmentId)}/letter-upload`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Patient-Token": session.sessionToken,
      },
      credentials: "omit",
      body: JSON.stringify({
        file_b64: ciphertextB64,
        mime,
        original_filename: filename,
        // doc_kind in the outer body so the bridge stores it in Firestore
        // for bridge_sync to read without decrypting (fast routing).
        doc_kind: "insurance_card",
      }),
    },
  );

  if (!resp.ok) {
    const body = await resp.text();
    if (resp.status === 413) {
      throw new Error(
        "The card photo is too large. Try a lower-resolution photo."
      );
    }
    throw new Error(`Insurance card upload failed (${resp.status}): ${body.slice(0, 200)}`);
  }
  return resp.json();
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
